/**
 * @fileoverview Trending topics Vue mixin.
 * Provides reusable methods for fetching trending topic data and refreshing
 * the trending topics list via GraphQL queries.
 * @module helperMethods/trendingTopics/mixins
 */

// @ts-nocheck
import { gql } from "@apollo/client/core";
import { TrendingIntentType } from "@/helperMethods/trendingTopics/type";

/**
 * Vue mixin providing trending topics data fetching methods.
 * Can be mixed into Vue components to access trending topics functionality.
 *
 * @example
 * import { mixin as trendingTopicsMixin } from '@/helperMethods/trendingTopics/mixins';
 * export default {
 *   mixins: [trendingTopicsMixin],
 *   async mounted() {
 *     const data = await this.getExamplesById(123);
 *   }
 * }
 */
export const mixin = {
  methods: {
    /**
     * Fetches trending topic examples by ID via GraphQL.
     *
     * @param {number} id - Trending topic ID
     * @returns {Promise<TrendingIntentType>} Trending intent data
     *
     * @example
     * const examples = await this.getExamplesById(123);
     * // Returns trending intent with examples
     */
    async getExamplesById(id: number): Promise<TrendingIntentType> {
      const {
        data: { data },
      } = await this.$apollo.query({
        query: gql`
          query ($id: Int) {
            data: getExamplesByTrendingId(id: $id)
          }
        `,
        variables: {
          id,
        },
      });
      return data;
    },
    /**
     * Re-fetches trending topics data based on current filter date range.
     * Dispatches appropriate Vuex actions based on whether date filter is set.
     *
     * @returns {Promise<void>}
     *
     * @example
     * await this.reCallApi();
     * // Refreshes trending topics with current filter settings
     */
    async reCallApi(): Promise<void> {
      const filterDate = this.$store.state.filterDate;
      if (filterDate && filterDate.every((date) => date)) {
        const [startDate, endDate] = filterDate;
        await Promise.all([
          this.$store.dispatch("TRENDING_TOPICS_FILTERD_BY_RANGE", { startDate, endDate }),
          this.$store.dispatch("TRENDING_TOPICS"),
        ]);
      } else {
        await Promise.all([
          this.$store.dispatch("TRENDING_TOPICS_FIRST_TIME"),
          this.$store.dispatch("TRENDING_TOPICS"),
        ]);
      }
    },
  },
};
